import React, { useMemo } from "react";

type MemoData = { date: string; content: string; category: string; time: string };

const formatDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const parseYmd = (s?: string | null) =>
  s ? new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10))) : new Date();

const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const weeksInMonth = (d: Date) => {
  const first = startOfMonth(d);
  const offset = first.getDay(); // 0 = Sun
  return Math.ceil((offset + daysInMonth(d.getFullYear(), d.getMonth())) / 7);
};

const gridStart = (d: Date) => {
  const first = startOfMonth(d);
  const s = new Date(first);
  s.setDate(1 - first.getDay());
  s.setHours(0, 0, 0, 0);
  return s;
};

// 같은 Y/M/D 비교
const sameYMD = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

interface Props {
  monthDate: string;
  selectedDate?: string | null;
  onSelectDate?: (date: string) => void;
  memos: Record<string, MemoData[]>;
  className?: string;
}

export default function MonthCalendar({ monthDate, selectedDate, onSelectDate, memos, className = "" }: Props) {
  // 월 기준 날짜 계산
  const base = useMemo(() => parseYmd(monthDate), [monthDate]);
  const weeks = useMemo(() => Math.max(4, Math.min(6, weeksInMonth(base))), [base]);
  const start = useMemo(() => gridStart(base), [base]);
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // 달력에 표시할 모든 셀 날짜 배열
  const cells = useMemo(() => {
    const total = weeks * 7;
    return Array.from({ length: total }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [start, weeks]);

  const title = `${base.getFullYear()}년 ${base.getMonth() + 1}월`;

  // 선택된 날짜 (없으면 오늘)
  const selected = useMemo(() => {
    const s = selectedDate ? parseYmd(selectedDate) : today;
    s.setHours(0, 0, 0, 0);
    return s;
  }, [selectedDate, today]);

  const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  return (
    <div className={`w-full h-full flex flex-col rounded-lg ${className}`}>
      {/* 타이틀 */}
      <div className="bg-[#D0E7AC] border-b border-[#81b334] py-2 text-center rounded-t-lg">
        <span className="text-[#333] font-semibold">{title}</span>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 bg-[#D0E7AC] box-border border-x border-transparent">
        {weekdays.map((w, i) => (
          <div
            key={w}
            className={[
              "box-border text-center py-2 text-[#333] font-medium",
              // 모든 세로선은 왼쪽에서 그린다
              i === 0 ? "border-l border-l-[#D0E7AC]" : "", // SUN: 왼쪽은 배경색
              i > 0 ? "border-l border-l-[#81b334]" : "", // MON~SAT: 왼쪽은 초록색
              i === 6 ? "border-r border-r-[#D0E7AC]" : "", // SAT: 오른쪽은 배경색(끝 정리용)
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {w}
          </div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="flex-1 min-h-0 border-x border-b border-gray-200 rounded-b-lg overflow-hidden">
        <div className="grid grid-cols-7 h-full" style={{ gridTemplateRows: `repeat(${weeks}, minmax(0, 1fr))` }}>
          {cells.map((d, i) => {
            const ds = formatDate(d);
            const isOtherMonth = d.getMonth() !== base.getMonth();
            const isSelected = sameYMD(selected, d);

            const col = i % 7; // 0:일 ~ 6:토
            const row = Math.floor(i / 7); // 0~(weeks-1)

            const dayMemos = memos[ds] || [];
            const hasImportant = dayMemos.some(x => x.category === "중요");
            const hasMemo = dayMemos.some(x => x.category === "메모");
            const hasDriverSchedule = dayMemos.some(x => x.category === "기사일정");

            return (
              <button
                key={ds}
                type="button"
                onClick={() => onSelectDate?.(ds)}
                className={[
                  "box-border relative w-full h-full text-left px-2 py-2 transition-colors",
                  "focus:outline-none focus-visible:outline-none",

                  // 내부 격자선
                  "border-t border-l border-gray-200",
                  row === 0 ? "border-t-0" : "",
                  col === 0 ? "border-l-0" : "",

                  // 배경/호버/커서 분기
                  isSelected
                    ? "bg-[#FEF5C3] hover:bg-[#FEF5C3] cursor-default" // 선택한 날
                    : isOtherMonth
                      ? "bg-[#f8f9fa] hover:bg-gray-100 cursor-pointer" // 이전/다음 달
                      : "bg-white hover:bg-gray-50 cursor-pointer", // 현재 달
                ].join(" ")}
                aria-selected={isSelected}
              >
                {/* 날짜 숫자 */}
                <div
                  className={[
                    "absolute top-3 left-2 font-medium leading-none",
                    isOtherMonth && !isSelected ? "text-gray-400" : "text-[#333]",
                  ].join(" ")}
                >
                  {d.getDate()}
                </div>

                {/* 메모/중요/기사일정 태그 */}
                {(hasMemo || hasImportant || hasDriverSchedule) && (
                  <div className="absolute left-2 right-2 bottom-2 flex gap-1 flex-wrap">
                    {hasMemo && (
                      <span
                        className="text-[10px] px-1 py-[2px] rounded"
                        style={{ backgroundColor: "rgba(0,133,255,.1)", color: "#0085FF" }}
                      >
                        메모
                      </span>
                    )}
                    {hasImportant && (
                      <span
                        className="text-[10px] px-1 py-[2px] rounded"
                        style={{ backgroundColor: "rgba(233,44,44,.1)", color: "#E92C2C" }}
                      >
                        중요
                      </span>
                    )}
                    {hasDriverSchedule && (
                      <span
                        className="text-[10px] px-1 py-[2px] rounded"
                        style={{ backgroundColor: "rgba(34,197,94,.1)", color: "#22C55E" }}
                      >
                        기사일정
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
